import { OrderBook, Security } from '../proto/generated/security'
import { Snapshot } from '../reducer/security.reducer'
import { RootState } from '../store'
import { getSecurityKey } from './security'

/** Native (unscaled) indicative equilibrium price, when the book is in auction. */
export const indicativePriceRaw = (orderBook: OrderBook | undefined): number | undefined => {
    const price = orderBook?.auction?.indicativePrice
    if (price == null || price <= 0) return undefined
    return price
}

export const indicativePriceRupees = (orderBook: OrderBook | undefined, priceMultiplier: number): number | null => {
    const raw = indicativePriceRaw(orderBook)
    if (raw == null) return null
    return raw / (priceMultiplier || 100)
}

/** Native last price used for display: indicative equilibrium when the book has one. */
export const displayLastTradedPrice = (state: RootState, securityKey: string, lastTradedPrice: number) =>
    indicativePriceRaw(state.securityStore.orderBooks[securityKey]) ?? lastTradedPrice

/** Display snapshot that uses indicative price in place of last traded price. */
export const snapshotWithIndicativePrice = (
    snapshot: Snapshot | undefined,
    orderBook: OrderBook | undefined
): Snapshot | undefined => {
    if (!snapshot) return snapshot
    const raw = indicativePriceRaw(orderBook)
    if (raw == null || raw === snapshot.lastTradedPrice) return snapshot
    return { ...snapshot, lastTradedPrice: raw }
}

export const snapshotWithIndicativePriceFromState = (
    state: RootState,
    securityKey: string,
    snapshot: Snapshot | undefined
): Snapshot | undefined => snapshotWithIndicativePrice(snapshot, state.securityStore.orderBooks[securityKey])

/** Overlay indicative prices onto expression leg snapshots before evaluating LTP. */
export const snapshotsWithIndicativePrices = (
    state: RootState,
    snapshots: Record<string, Snapshot>,
    securities: Security[]
): Record<string, Snapshot> => {
    let next = snapshots
    for (const security of securities) {
        const key = getSecurityKey(security.exchange, security.code)
        const current = next[key]
        const overlaid = snapshotWithIndicativePrice(current, state.securityStore.orderBooks[key])
        if (!current || overlaid === current) continue
        if (next === snapshots) next = { ...snapshots }
        next[key] = overlaid!
    }
    return next
}
